import type { Components, Theme } from "@mui/material/styles";
import { capitalize } from "lodash";

/**
 * Gerador centralizado de estilos por cor para componentes MUI.
 *
 * Aceita qualquer cor do palette (primary, secondary, success, warning etc.)
 * e mantém suporte para a variante custom `favorite`.
 */

type ColorToken = {
	main: string;
	light: string;
	dark: string;
	contrastText: string;
};

type SemanticColors = Record<string, ColorToken>;

type ContainedStrategy =
	| {
			kind: "solid";
			backgroundColor?: string;
			hoverBackgroundColor?: string;
			boxShadow?: string;
	  }
	| {
			kind: "gradient";
			from: string;
			to: string;
			hoverFilter?: string;
			boxShadow?: string;
	  };

type OutlinedStrategy = {
	borderAlpha?: number;
	hoverAlpha?: number;
	color?: string;
	hoverColor?: string;
};

type TextStrategy = {
	hoverAlpha?: number;
	color?: string;
	hoverColor?: string;
};

type CapsuleStyle = "filled" | "outlined";

export type ButtonColorStrategy = {
	contained?: ContainedStrategy;
	outlined?: OutlinedStrategy;
	text?: TextStrategy;
};

export type ButtonStrategyMap = Partial<Record<string, ButtonColorStrategy>>;

/* ── Helpers ───────────────────────────────────────────────── */
function hexToRgba(hex: string, alpha: number): string {
	const h = hex.replace("#", "");
	const r = parseInt(h.slice(0, 2), 16);
	const g = parseInt(h.slice(2, 4), 16);
	const b = parseInt(h.slice(4, 6), 16);
	return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function buildGradient(from: string, to: string): string {
	return `linear-gradient(135deg, ${from} 0%, ${to} 100%)`;
}

function buildCapsuleGradient(c: ColorToken, colorName: string): string {
	if (colorName !== "primary") return `linear-gradient(140deg, ${c.light} 0%, ${c.main} 52%, ${c.dark} 100%)`;

	return buildGradient(c.main, c.dark);
}

function buildContainedStyle(c: ColorToken, containedStrategy?: ContainedStrategy): Record<string, unknown> {
	if (containedStrategy?.kind === "gradient") {
		const gradient = buildGradient(containedStrategy.from, containedStrategy.to);
		const hoverFilter = containedStrategy.hoverFilter ?? "saturate(1.08) brightness(0.94)";

		return {
			backgroundImage: gradient,
			backgroundColor: containedStrategy.from,
			backgroundSize: "160% 160%",
			backgroundPosition: "0% 50%",
			color: c.contrastText,
			border: "none",
			boxShadow: containedStrategy.boxShadow ?? "none",
			"&:hover, &:active, &.Mui-focusVisible": {
				backgroundImage: gradient,
				backgroundPosition: "100% 50%",
				filter: hoverFilter,
			},
		};
	}

	return {
		backgroundColor: containedStrategy?.backgroundColor ?? c.main,
		color: c.contrastText,
		border: "none",
		boxShadow: containedStrategy?.boxShadow ?? "none",
		"&:hover, &:active, &.Mui-focusVisible": {
			backgroundColor: containedStrategy?.hoverBackgroundColor ?? c.dark,
		},
	};
}

function buildOutlinedStyle(c: ColorToken, outlinedStrategy?: OutlinedStrategy): Record<string, unknown> {
	const outlinedBorderAlpha = outlinedStrategy?.borderAlpha ?? 0.5;
	const outlinedHoverAlpha = outlinedStrategy?.hoverAlpha ?? 0.06;
	const outlinedColor = outlinedStrategy?.color ?? c.main;
	const outlinedHoverColor = outlinedStrategy?.hoverColor ?? c.dark;

	return {
		borderColor: hexToRgba(c.main, outlinedBorderAlpha),
		color: outlinedColor,
		backgroundColor: "transparent",
		"&:hover, &:active, &.Mui-focusVisible": {
			borderColor: c.main,
			backgroundColor: hexToRgba(c.main, outlinedHoverAlpha),
			color: outlinedHoverColor,
		},
	};
}

function buildTextStyle(c: ColorToken, textStrategy?: TextStrategy): Record<string, unknown> {
	const textColor = textStrategy?.color ?? c.main;
	const textHoverColor = textStrategy?.hoverColor ?? c.dark;
	const textHoverAlpha = textStrategy?.hoverAlpha ?? 0.06;

	return {
		color: textColor,
		"&:hover, &:active, &.Mui-focusVisible": {
			backgroundColor: hexToRgba(c.main, textHoverAlpha),
			color: textHoverColor,
		},
	};
}

function buildCapsuleStyle(c: ColorToken, style: CapsuleStyle, colorName: string): Record<string, unknown> {
	const capsuleGradient = buildCapsuleGradient(c, colorName);
	const useGradientOnCapsule = colorName !== "primary";
	const iconSlotBase = {
		width: 26,
		height: 26,
		borderRadius: 999,
		display: "inline-flex",
		alignItems: "center",
		justifyContent: "center",
		flexShrink: 0,
		transition: "all 0.16s ease",
		margin: 0,
	} as const;

	const shared = {
		borderRadius: 999,
		letterSpacing: "0.06em",
		textTransform: "uppercase" as const,
		fontWeight: 600,
		gap: 6,
		minHeight: 32,
		padding: "0 9px 0 13px",
		"&.MuiButton-sizeExtraSmall": {
			minHeight: 20,
			minWidth: 20,
			padding: "0 5px 0 7px",
			fontSize: "0.625rem",
			letterSpacing: "0.02em",
			gap: 2,
			"& .MuiButton-startIcon, & .MuiButton-endIcon": {
				width: 14,
				height: 14,
			},
			"& .MuiButton-startIcon": {
				marginRight: 2,
				marginLeft: -1,
			},
			"& .MuiButton-endIcon": {
				marginLeft: 2,
				marginRight: -1,
			},
			"& .MuiButton-startIcon > *, & .MuiButton-endIcon > *": {
				fontSize: "0.625rem",
				width: "0.625rem",
				height: "0.625rem",
			},
		},
		"&.MuiButton-sizeSmall": {
			minHeight: 30,
			padding: "0 8px 0 12px",
			"& .MuiButton-startIcon, & .MuiButton-endIcon": {
				width: 20,
				height: 20,
			},
			"& .MuiButton-startIcon > *, & .MuiButton-endIcon > *": {
				fontSize: "0.82rem",
				width: "0.82rem",
				height: "0.82rem",
			},
		},
		"&.MuiButton-sizeMedium": {
			minHeight: 34,
			padding: "0 10px 0 15px",
			"& .MuiButton-startIcon, & .MuiButton-endIcon": {
				width: 23,
				height: 23,
			},
			"& .MuiButton-startIcon > *, & .MuiButton-endIcon > *": {
				fontSize: "0.92rem",
				width: "0.92rem",
				height: "0.92rem",
			},
		},
		"&.MuiButton-sizeLarge": {
			minHeight: 39,
			padding: "0 12px 0 18px",
			"& .MuiButton-startIcon, & .MuiButton-endIcon": {
				width: 26,
				height: 26,
			},
			"& .MuiButton-startIcon > *, & .MuiButton-endIcon > *": {
				fontSize: "1.02rem",
				width: "1.02rem",
				height: "1.02rem",
			},
		},
		"& .MuiButton-startIcon, & .MuiButton-endIcon": iconSlotBase,
		"& .MuiButton-startIcon": {
			marginRight: 8,
			marginLeft: -2,
		},
		"& .MuiButton-endIcon": {
			marginLeft: 8,
			marginRight: -2,
		},
		"&.Mui-focusVisible": {
			outline: `2px solid ${hexToRgba(c.main, 0.28)}`,
			outlineOffset: 2,
		},
	};

	if (style === "outlined")
		return {
			...shared,
			border: `1px solid ${hexToRgba(c.main, 0.75)}`,
			backgroundColor: "#FFFFFF",
			color: c.dark,
			boxShadow: "none",
			"& .MuiButton-startIcon, & .MuiButton-endIcon": {
				...iconSlotBase,
				color: c.contrastText,
				backgroundImage: useGradientOnCapsule ? capsuleGradient : "none",
				backgroundColor: c.main,
				backgroundSize: useGradientOnCapsule ? "200% 200%" : "100% 100%",
				backgroundPosition: useGradientOnCapsule ? "0% 50%" : "50% 50%",
				border: `1px solid ${hexToRgba(c.main, 0.2)}`,
			},
			"&:hover, &:active": {
				backgroundColor: hexToRgba(c.main, 0.06),
				borderColor: c.main,
				boxShadow: `0 0 0 1px ${hexToRgba(c.main, 0.18)}`,
				...(useGradientOnCapsule && {
					"& .MuiButton-startIcon, & .MuiButton-endIcon": {
						backgroundPosition: "100% 50%",
					},
				}),
			},
		};

	return {
		...shared,
		border: `1px solid ${hexToRgba(c.main, 0.35)}`,
		backgroundImage: useGradientOnCapsule ? capsuleGradient : "none",
		backgroundColor: c.main,
		backgroundSize: useGradientOnCapsule ? "210% 210%" : "100% 100%",
		backgroundPosition: useGradientOnCapsule ? "0% 50%" : "50% 50%",
		color: c.contrastText,
		boxShadow: "none",
		"& .MuiButton-startIcon, & .MuiButton-endIcon": {
			...iconSlotBase,
			color: c.contrastText,
			backgroundColor: "rgba(255, 255, 255, 0.16)",
			border: "1px solid rgba(255, 255, 255, 0.66)",
		},
		"&:hover, &:active": useGradientOnCapsule
			? {
					backgroundPosition: "100% 50%",
					filter: "saturate(1.11) brightness(0.94)",
					boxShadow: `0 0 0 1px ${hexToRgba(c.main, 0.3)}`,
				}
			: {
					backgroundColor: c.dark,
					boxShadow: `0 0 0 1px ${hexToRgba(c.main, 0.3)}`,
				},
	};
}

/* ── Button variants ───────────────────────────────────────── */
export function buildButtonVariants(
	semanticColors: SemanticColors,
	strategyMap: ButtonStrategyMap = {},
): Components<Theme>["MuiButton"] {
	const variants: NonNullable<Components<Theme>["MuiButton"]>["variants"] = [];
	variants.push({
		props: { size: "extraSmall" },
		style: {
			minHeight: 20,
			minWidth: 20,
			padding: "1px 6px",
			fontSize: "0.625rem",
			lineHeight: 1.2,
			"& .MuiButton-startIcon": {
				marginRight: 2,
				marginLeft: -1,
			},
			"& .MuiButton-endIcon": {
				marginRight: -1,
				marginLeft: 2,
			},
			"& .MuiButton-startIcon > *, & .MuiButton-endIcon > *": {
				fontSize: "0.625rem",
				width: "0.625rem",
				height: "0.625rem",
			},
		},
	});

	for (const [name, c] of Object.entries(semanticColors)) {
		const strategy = strategyMap[name];

		variants.push({
			props: { variant: "contained" as const, color: name as any },
			style: buildContainedStyle(c, strategy?.contained),
		});

		variants.push({
			props: { variant: "outlined" as const, color: name as any },
			style: buildOutlinedStyle(c, strategy?.outlined),
		});

		variants.push({
			props: { variant: "text" as const, color: name as any },
			style: buildTextStyle(c, strategy?.text),
		});

		variants.push({
			props: { variant: "capsule" as const, color: name as any },
			style: buildCapsuleStyle(c, "filled", name),
		});

		variants.push({
			props: { variant: "capsuleOutlined" as const, color: name as any },
			style: buildCapsuleStyle(c, "outlined", name),
		});
	}

	if (semanticColors.favorite) {
		const fav = semanticColors.favorite;
		const favoriteContained = strategyMap.favorite?.contained;
		const favoriteBackground =
			favoriteContained?.kind === "gradient"
				? buildGradient(favoriteContained.from, favoriteContained.to)
				: fav.main;
		const favoriteHoverFilter =
			favoriteContained?.kind === "gradient"
				? (favoriteContained.hoverFilter ?? "saturate(1.08) brightness(0.94)")
				: "none";

		variants.push({
			props: { variant: "favorite" as const },
			style: {
				border: `1px solid ${fav.main}`,
				color: "#FFFFFF",
				background: favoriteBackground,
				borderRadius: 8,
				boxShadow: favoriteContained?.boxShadow ?? "none",
				"&:hover, &:active, &.Mui-focusVisible": {
					background:
						favoriteContained?.kind === "gradient"
							? buildGradient(favoriteContained.from, favoriteContained.to)
							: fav.dark,
					borderColor: fav.dark,
					filter: favoriteHoverFilter,
				},
			},
		});
	}

	return { variants };
}

/* ── Button color styleOverrides ───────────────────────────── */
export function buildButtonStyleOverrides(
	semanticColors: SemanticColors,
	strategyMap: ButtonStrategyMap = {},
): Record<string, Record<string, unknown>> {
	const styleOverrides: Record<string, Record<string, unknown>> = {};

	for (const [name, c] of Object.entries(semanticColors)) {
		const capitalName = capitalize(name);
		const strategy = strategyMap[name];

		styleOverrides[`contained${capitalName}`] = buildContainedStyle(c, strategy?.contained);
		styleOverrides[`outlined${capitalName}`] = buildOutlinedStyle(c, strategy?.outlined);
		styleOverrides[`text${capitalName}`] = buildTextStyle(c, strategy?.text);
	}

	return styleOverrides;
}

/* ── IconButton color overrides ────────────────────────────── */
export function buildIconButtonOverrides(semanticColors: SemanticColors): Components<Theme>["MuiIconButton"] {
	const styleOverrides: Record<string, Record<string, unknown>> = {};

	for (const [name, c] of Object.entries(semanticColors)) {
		const capitalName = capitalize(name);

		styleOverrides[`color${capitalName}`] = {
			color: c.main,
			"&:hover": {
				backgroundColor: hexToRgba(c.main, 0.08),
			},
		};
	}

	return { styleOverrides };
}

/* ── Chip color overrides ──────────────────────────────────── */
const CHIP_SIZE_STYLES: Record<string, Record<string, unknown>> = {
	"extra-small": {
		height: 20,
		fontSize: "0.625rem",
		"& .MuiChip-label": {
			paddingLeft: 6,
			paddingRight: 6,
		},
		"& .MuiChip-icon, & .MuiChip-deleteIcon": {
			fontSize: "0.875rem",
		},
	},
	extraSmall: {
		height: 20,
		fontSize: "0.625rem",
		"& .MuiChip-label": {
			paddingLeft: 6,
			paddingRight: 6,
		},
		"& .MuiChip-icon, & .MuiChip-deleteIcon": {
			fontSize: "0.875rem",
		},
	},
	small: {
		height: 24,
		fontSize: "0.6875rem",
		"& .MuiChip-label": {
			paddingLeft: 8,
			paddingRight: 8,
		},
	},
	medium: {
		height: 28,
		fontSize: "0.75rem",
		"& .MuiChip-label": {
			paddingLeft: 10,
			paddingRight: 10,
		},
	},
	large: {
		height: 34,
		fontSize: "0.8125rem",
		"& .MuiChip-label": {
			paddingLeft: 12,
			paddingRight: 12,
		},
	},
};

function buildChipContainedStyle(c: ColorToken): Record<string, unknown> {
	return {
		backgroundColor: c.main,
		color: c.contrastText,
		"&:hover": {
			backgroundColor: c.dark,
		},
		"& .MuiChip-deleteIcon": {
			color: hexToRgba(c.contrastText, 0.7),
			"&:hover": { color: c.contrastText },
		},
	};
}

function buildChipOutlinedStyle(c: ColorToken): Record<string, unknown> {
	return {
		borderColor: hexToRgba(c.main, 0.5),
		color: c.main,
		backgroundColor: hexToRgba(c.main, 0.06),
		"&:hover": {
			backgroundColor: hexToRgba(c.main, 0.12),
		},
		"& .MuiChip-deleteIcon": {
			color: hexToRgba(c.main, 0.6),
			"&:hover": { color: c.main },
		},
	};
}

export function buildChipVariants(semanticColors: SemanticColors): Components<Theme>["MuiChip"] {
	const variants: NonNullable<Components<Theme>["MuiChip"]>["variants"] = [];

	for (const [size, style] of Object.entries(CHIP_SIZE_STYLES))
		variants.push({
			props: { size: size as any },
			style,
		});

	for (const [name, c] of Object.entries(semanticColors)) {
		variants.push({
			props: { variant: "contained" as any, color: name as any },
			style: buildChipContainedStyle(c),
		});

		variants.push({
			props: { variant: "outlined" as const, color: name as any },
			style: buildChipOutlinedStyle(c),
		});
	}

	return { variants };
}

export function buildChipOverrides(semanticColors: SemanticColors): Components<Theme>["MuiChip"] {
	const styleOverrides: Record<string, Record<string, unknown>> = {};

	for (const [name, c] of Object.entries(semanticColors)) {
		const capitalName = capitalize(name);

		styleOverrides[`filled${capitalName}`] = buildChipContainedStyle(c);
		styleOverrides[`outlined${capitalName}`] = buildChipOutlinedStyle(c);
	}

	return { styleOverrides };
}
